import './MenuPage.css'
import SEO from '../../components/SEO/SEO';
import { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { ApiService } from '../../services/api.service';
import { getBackendImageUrl } from '../../utils/imageUtils';

function MenuPage() {
    const [searchParams] = useSearchParams();
    const [activeMenu, setActiveMenu] = useState('Menu')
    const [isVisible, setIsVisible] = useState(false)
    const featuredRef = useRef(null)
    const [menuItems, setMenuItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchMenuItems = async () => {
            try {
                const items = await ApiService.getMenuItems();
                // Remplacer "main" par "menu principal" et trier les items
                const modifiedItems = items.map(item => ({
                    ...item,
                    title: item.title === 'main' ? 'Menu principal' : item.title
                })).sort((a, b) => {
                    // Mettre "menu principal" en premier
                    if (a.title === 'Menu principal') return -1;
                    if (b.title === 'Menu principal') return 1;
                    return 0;
                });
                setMenuItems(modifiedItems);
                
                // Attendre que les items soient chargés avant de définir le menu actif et l'item sélectionné
                const mainMenu = modifiedItems.find(item => item.title === 'Menu principal');
                if (mainMenu) {
                    setActiveMenu('Menu principal');
                    setSelectedItem(mainMenu);

                }
            } catch (error) {
                console.error('Erreur lors du chargement des items:', error);
            }
        };

        fetchMenuItems();
    }, []);

    useEffect(() => {
        // Récupérer le paramètre 'menu' de l'URL
        const menuParam = searchParams.get('menu');
        if (menuParam) {
            // Vérifier si le menu existe dans nos items
            const validMenu = menuItems.find(item => item.category === menuParam);
            if (validMenu) {
                setActiveMenu(menuParam);
            }
        }
    }, [searchParams, menuItems]);

    useEffect(() => {
        setIsVisible(true)
        
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('visible')
                }
            },
            { threshold: 0.1 }
        )

        if (featuredRef.current) {
            observer.observe(featuredRef.current)
        }

        return () => observer.disconnect()
    }, [])

    const handleMenuChange = (title) => {
        setActiveMenu('')  // Reset pour l'animation
        setTimeout(() => setActiveMenu(title), 50)  // Réapplique après un court délai
    }

    const handleItemClick = (item) => {
        setSelectedItem(item);
    };

    const handleImageClick = (e, image) => {
        e.stopPropagation();
        setSelectedImage(image);
    };

    const filteredItems = menuItems.filter(item => item.category === activeMenu);

    return (
        <>
            <SEO 
                title="Notre Carte"
                description="Découvrez notre carte gastronomique : plats traditionnels français, vins d'exception et desserts maison. Menu du jour, carte des vins et suggestions du chef."
                keywords="menu restaurant Tours, carte gastronomique, plats français, vins Tours, menu du jour, cuisine traditionnelle"
                url="/menu"
            />
            <div className="menu-page">
                <div className={`hero-banner ${isVisible ? 'visible' : ''}`}>
                <h1>NOTRE CARTE</h1>
                <div className="hero-decoration"></div>
            </div>
            
            <div className="menu-container">
                <div className="featured-section" ref={featuredRef}>
                    <div className="featured-content">
                        <div className="featured-image-container">
                            <div className="featured-badge">Suggestion du Chef</div>
                            <img src="./images/tg_1.png" alt="Charlotte aux fraises" />
                        </div>
                        <div className="featured-text">
                            <span className="featured-label">En Vedette</span>
                            <h2>Charlotte aux Fraises</h2>
                            <div className="price-tag">12€</div>
                            <p className="featured-description">
                                Une délicate charlotte aux fraises fraîches de saison, 
                                accompagnée d'un coulis de fruits rouges et d'une 
                                chantilly maison à la vanille de Madagascar.
                            </p>
                            <div className="featured-footer">
                                <span className="seasonal-badge">Dessert de Saison</span>
                                <span className="availability">Disponible jusqu'au 22/01/2056</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="dynamic-section">
                    <nav className="menu-navigation">
                        <div className="menu-nav-container">
                            {Array.from(new Set(menuItems.map(item => item.title))).map((title) => (
                                <button 
                                    key={title}
                                    className={`menu-nav-item ${activeMenu === title ? 'active' : ''}`}
                                    onClick={async () => {
                                        setIsLoading(true);
                                        setActiveMenu(title);
                                        const newSelectedItem = menuItems.find(item => item.title === title);
                                        setSelectedItem(newSelectedItem);
                                        
                                        // Simuler un petit délai pour le loader (optionnel)
                                        await new Promise(resolve => setTimeout(resolve, 300));
                                        setIsLoading(false);
                                    }}
                                >
                                    <span>{title}</span>
                                </button>
                            ))}
                        </div>
                    </nav>

                    <div className="selected-display">
                        <div className={`selected-card ${activeMenu ? 'visible' : ''}`}>
                            <div className="selected-image" onClick={(e) => handleImageClick(e, selectedItem?.path)}>
                                {isLoading ? (
                                    <div className="menu-loading">
                                        <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                                        <p>Chargement...</p>
                                    </div>
                                ) : selectedItem?.path ? (
                                    <img src={getBackendImageUrl(selectedItem.path)} alt={selectedItem.title || 'Menu'} />
                                ) : (
                                    <div className="no-image-placeholder">
                                        <p>Aucune image disponible</p>
                                    </div>
                                )}
                                <div className="selected-overlay">
                                    <h3>{selectedItem?.title || 'Chargement...'}</h3>
                                    <span className="subtitle">{selectedItem?.subtitle || ''}</span>
                                    <p>{selectedItem?.description || ''}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {selectedImage && (
                <div className="lightbox" onClick={() => setSelectedImage(null)}>
                    <button className="close-button" onClick={() => setSelectedImage(null)}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <img src={getBackendImageUrl(selectedImage)} alt="Image du menu" />
                </div>
            )}
            </div>
        </>
    )
}

export default MenuPage